import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Quote, ArrowRight } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { listCaseStudies } from '../lib/api';

export default function CaseStudies() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    listCaseStudies()
      .then((d) => setStudies(d))
      .catch(() => setStudies([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#FAF7F5]">
      <HeroBanner
        image="/hero/case-studies.jpg"
        overlay="default"
        height="min-h-[460px] lg:min-h-[540px]"
      >
        <div className="pt-[120px] lg:pt-[140px] pb-12">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-end erbity-fade-up">
              <div className="lg:col-span-8">
                <EyebrowLabel>Case Studies</EyebrowLabel>
                <SectionTitle as="h1" className="mt-6 text-[44px] lg:text-[68px] leading-[1.04]">
                  Wins that shipped <span className="italic text-[#7E2E4A]">silicon</span>.
                </SectionTitle>
              </div>
              <div className="lg:col-span-4 erbity-fade-up-1">
                <p className="text-[16px] text-[#3B333A] leading-relaxed">
                  A snapshot of hiring programs Erbity has delivered &mdash; the role,
                  the node, the time-to-fill and the outcome. Client names withheld
                  for confidentiality.
                </p>
              </div>
            </div>
          </Container>
        </div>
      </HeroBanner>

      <section className="pb-16 lg:pb-20">
        <Container>
          {loading ? (
            <div className="text-[#7A6A72] text-[14px]">Loading case studies&hellip;</div>
          ) : studies.length === 0 ? (
            <div className="text-[#7A6A72] text-[14px]">No case studies yet.</div>
          ) : (
            <div className="space-y-6">
              {studies.map((cs, i) => (
                <article key={cs.id} className="grid lg:grid-cols-12 gap-8 bg-white rounded-3xl border border-[#EADDE2] p-8 lg:p-12">
                  <div className="lg:col-span-4">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">Case 0{i + 1} &middot; {cs.engagement}</div>
                    <h2 className="mt-4 font-serif text-[26px] lg:text-[32px] leading-tight text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{cs.title}</h2>
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <MetaCell label="Industry" value={cs.industry} />
                      <MetaCell label="Time-to-fill" value={cs.time_to_fill} />
                      <MetaCell label="Role" value={cs.role} />
                      <MetaCell label="Node" value={cs.node || '—'} />
                    </div>
                  </div>
                  <div className="lg:col-span-8 border-t lg:border-t-0 lg:border-l border-[#EADDE2] pt-8 lg:pt-0 lg:pl-12">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A6A72]">Outcome</div>
                    <p className="mt-3 text-[16px] text-[#1A1420] leading-relaxed">{cs.outcome}</p>
                    {cs.quote && (
                      <figure className="mt-8 relative bg-[#FAF7F5] rounded-2xl border border-[#EADDE2] p-6">
                        <Quote size={18} className="text-[#C4506F] mb-3" />
                        <blockquote className="text-[15px] italic text-[#1A1420] leading-relaxed">“{cs.quote}”</blockquote>
                        <figcaption className="mt-4 text-[12.5px] text-[#7A6A72]">&mdash; {cs.quote_author}</figcaption>
                      </figure>
                    )}
                    {cs.tags?.length > 0 && (
                      <div className="mt-6 flex flex-wrap gap-2">
                        {cs.tags.map((t) => (
                          <span key={t} className="text-[12px] px-3 py-1 rounded-full bg-[#F2E3E8] text-[#7E2E4A] border border-[#EADDE2]">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-8 flex items-center gap-4 text-[13.5px] text-[#4E434A]">
                      <span>Client: {cs.client_type}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="mt-16 rounded-3xl bg-[#1A1420] text-white p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <SectionTitle dark className="text-[26px] lg:text-[34px] leading-tight">Have a similar hiring problem?</SectionTitle>
              <p className="mt-3 text-[15px] text-white/60 max-w-lg">Tell us the role, the node and the deadline &mdash; we&rsquo;ll return a domain-vetted shortlist within 72 hours.</p>
            </div>
            <Link to="/contact" className="group inline-flex items-center gap-2 bg-white text-[#1A1420] text-[15px] font-medium pl-6 pr-2 py-2 rounded-full hover:bg-[#F2E3E8] transition-colors">
              <span className="py-1">Start a conversation</span>
              <span className="w-10 h-10 rounded-full bg-[#C4506F] text-white flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight size={16} />
              </span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#7A6A72]">{label}</div>
      <div className="mt-1 text-[14px] text-[#1A1420] font-medium">{value}</div>
    </div>
  );
}

export { CaseStudies };

// Reusable home strip
export function CaseStudyHomeStrip() {
  const [studies, setStudies] = useState([]);
  useEffect(() => { listCaseStudies().then(setStudies).catch(() => {}); }, []);
  if (studies.length === 0) return null;
  return (
    <section className="py-14 lg:py-16 bg-[#FAF7F5]">
      <Container>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <EyebrowLabel>Selected wins</EyebrowLabel>
            <SectionTitle className="mt-5 text-[36px] lg:text-[52px]">Hires that closed silicon.</SectionTitle>
          </div>
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-[15px] text-[#1A1420] border-b border-[#1A1420]/30 hover:border-[#7E2E4A] pb-1 self-start">
            View all case studies <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {studies.slice(0, 3).map((cs, i) => (
            <Link to="/case-studies" key={cs.id} className="group bg-white rounded-2xl border border-[#EADDE2] p-7 hover:border-[#C4506F] transition-colors flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">Case 0{i + 1} &middot; {cs.engagement}</div>
              <h3 className="mt-4 font-serif text-[22px] leading-tight text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{cs.title}</h3>
              <div className="mt-5 grid grid-cols-2 gap-3 text-[12.5px]">
                <div><div className="text-[#7A6A72] uppercase tracking-[0.18em] text-[10px]">Node</div><div className="text-[#1A1420] mt-0.5">{cs.node || '—'}</div></div>
                <div><div className="text-[#7A6A72] uppercase tracking-[0.18em] text-[10px]">Time-to-fill</div><div className="text-[#1A1420] mt-0.5">{cs.time_to_fill}</div></div>
              </div>
              <p className="mt-5 pt-5 border-t border-[#EADDE2] text-[13.5px] text-[#4E434A] leading-relaxed line-clamp-4">{cs.outcome}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] text-[#7E2E4A] group-hover:gap-2.5 transition-all">Read more <ArrowUpRight size={13} /></div>
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}
